<template>
    <div class="ds-data-table-wrapper">
        <DataTable
            ref="dataTable"
            v-bind="$attrs"
            :editMode="editMode"
            :first
            :lazy
            :loading
            :pt="DsDatatablePassThroughOptions"
            :rows
            :totalRecords
            :value
            @page="handlePrimeVuePageEvent"
            @sort="onSortOrderChange"
        >
            <template #loadingicon>
                <DsSpinner :size="totalRecords <= 3 ? 'xl' : 'xxl'" />
            </template>
            <template
                v-for="(_, name) in slots"
                :key="name"
                #[name]="slotProps"
            >
                <slot
                    :name="name as keyof DataTableSlots"
                    v-bind="slotProps || {}"
                />
            </template>
        </DataTable>
        <DsPaginator
            v-if="pagination"
            v-model:page="page"
            v-model:rows="rows"
            :first
            :totalRecords
        />
    </div>
</template>

<script setup lang="ts">
/**
 * Context: This DS component is a wrapper around Primevue's DataTable with a custom paginator.
 * It is designed for server-side pagination using PrimeVue's `lazy` mode.
 */
import DsSpinner from '@/components/design-system/spinner/DsSpinner.vue';
import DsPaginator from '@/components/design-system/table/DsPaginator.vue';
import type {
    DataTableExportCSVOptions,
    DataTablePageEvent,
    DataTablePassThroughOptions,
    DataTableProps,
    DataTableSlots,
    DataTableSortEvent,
} from 'primevue/datatable';
import DataTable from 'primevue/datatable';
import type { PassThrough } from 'primevue/ts-helpers';
import { computed, ref, useSlots, useTemplateRef } from 'vue';
import type { DsDataTableProps, SortState } from './datatable.types';

defineSlots<DataTableSlots>();

const props = withDefaults(defineProps<DsDataTableProps>(), {
    totalRecords: 0,
    pagination: true,
    lazy: true,
    clickableRows: false,
});

const sortState = ref<SortState>();

const dataTable = useTemplateRef('dataTable');

const slots = useSlots();
const page = defineModel<number>('page', { required: true });
const rows = defineModel<number>('rows', { required: true });
const first = computed(() => page.value * rows.value);

const handlePrimeVuePageEvent = (event: DataTablePageEvent) => {
    page.value = event.page;
    rows.value = event.rows;
};

const onSortOrderChange = (event: DataTableSortEvent) => {
    const field = typeof event.sortField === 'string' ? event.sortField : 'name';
    sortState.value = {
        field,
        order: event.sortOrder === 1 ? 'asc' : 'desc',
    };
};

const exportCSV = (options?: DataTableExportCSVOptions, data?: NonNullable<DataTableProps['value']>) =>
    dataTable.value?.exportCSV(options, data);

defineExpose({ exportCSV });

const DsDatatablePassThroughOptions: PassThrough<DataTablePassThroughOptions> = {
    root: {
        class: 'w-full min-h-24 shadow-none',
    },
    wrapper: {
        class: {
            'w-full rounded-lg border border-ds-primary-200': true,
            'rounded-b-none': props.pagination,
        },
    },
    paginator: {
        root: { class: 'hidden' },
    },
    sortBadge: 'hidden',
    loadingOverlay: {
        class: 'pt-9 z-10',
    },
    loadingIcon: {
        class: 'animate-spin',
    },
    emptyMessageCell: {
        class: 'empty-message-cell',
    },
    bodyRow: () => {
        return { class: [props.clickableRows ? 'clickable-row' : ''] };
    },
};
</script>

<style scoped lang="scss">
:deep([data-pc-name='datatable']) {
    // Overwrites PrimeVue's default table borders
    min-height: theme('spacing.20') !important;
}
:deep(th) {
    background-color: theme('colors.ds.gray.100') !important;
    border-bottom: 1px solid theme('colors.ds.gray.200') !important;
    &[data-p-sortable-column='true'] {
        @apply cursor-pointer;
    }
    span[data-pc-section='sort'] svg {
        height: 10px !important;
        color: theme('colors.ds.gray.700') !important;
    }
}

:deep(tr:only-child td:only-child:empty) {
    border: none !important;
}
:deep(tr:last-child td) {
    border-bottom: none !important;
}

:deep(.clickable-row td:not(.click-disabled)) {
    @apply cursor-pointer;
}

:deep(tr.clickable-row) {
    @apply bg-white hover:bg-semantic-secondary-hover active:bg-semantic-secondary-pressed;
}

@mixin cell-padding {
    padding: theme('spacing.3') theme('spacing.4') !important;
}

@mixin cell-border {
    border-bottom: 1px solid theme('colors.ds.gray.200') !important;
    border-left: none !important;
    border-right: none !important;
}

:deep(td[data-pc-section='bodycell']) {
    //Overwrites PrimeVue's default cell borders
    @include cell-padding();
    @include cell-border();

    @apply whitespace-nowrap;
}

:deep([data-pc-section='emptymessagecell']) {
    @include cell-padding();
    @include cell-border();
    @apply whitespace-nowrap;

    height: theme('spacing.12') !important;
}
</style>
